// J22-B03 — contrast-aware neutralization of authored inline text colours.
// Authored/imported content carries inline `color:` values picked against a
// white page; on the non-light theme surfaces near-black inks become
// invisible. The pass keeps any ink that still reads (≥ 3:1, WCAG non-text
// threshold) against its authored background pair or the theme surface, and
// clears the rest so they inherit the token foreground — the original value
// is stashed in `data-ap-authored-ink` for the reverse pass when the theme
// flips back to light at runtime.
import { getToken, oklchToSrgb } from '@/lib/tokens';

export const AUTHORED_INK_ATTR = 'data-ap-authored-ink';

/** WCAG non-text threshold — bright authored inks (reds/blues) survive on dark. */
const MIN_CONTRAST = 3;

export interface InkColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
// Legacy comma AND modern space syntax; optional `,`/`/` alpha (number or %).
const RGB_RE =
  /^rgba?\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/;
// hsl()/hsla(), legacy comma + modern space syntax; deg-only hue (the v3
// editor emits `hsl(210, 75%, 60%)` on its colour presets).
const HSL_RE =
  /^hsla?\(\s*([\d.]+)(?:deg)?\s*[,\s]\s*([\d.]+)%\s*[,\s]\s*([\d.]+)%\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/;

/** transparent / hsl() / hex / rgb() / rgba() / oklch() → sRGB channels; anything else → null (safe skip). */
export const parseInkColor = (value: string | null | undefined): InkColor | null => {
  if (!value) return null;
  const v = value.trim();
  // `transparent` = fully clear (CSS keyword for rgba(0,0,0,0)) — callers
  // treat a=0 as "no paint" (J22-B03: the v3 editor stamps
  // `background-color:transparent` on default-text spans).
  if (/^transparent$/i.test(v)) return { r: 0, g: 0, b: 0, a: 0 };
  const hsl = v.match(HSL_RE);
  if (hsl) {
    const h = ((parseFloat(hsl[1]) % 360) + 360) % 360;
    const s = Math.min(100, Math.max(0, parseFloat(hsl[2]))) / 100;
    const l = Math.min(100, Math.max(0, parseFloat(hsl[3]))) / 100;
    let a = 1;
    if (hsl[4] !== undefined) {
      a = hsl[4].endsWith('%') ? parseFloat(hsl[4]) / 100 : parseFloat(hsl[4]);
      if (!Number.isFinite(a)) return null;
      a = Math.min(1, Math.max(0, a));
    }
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    const [r1, g1, b1] =
      h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] :
      h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255),
      a,
    };
  }
  const hex = v.match(HEX_RE);
  if (hex) {
    const h = hex[1].length === 3 ? [...hex[1]].map((c) => c + c).join('') : hex[1];
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
  }
  const rgb = v.match(RGB_RE);
  if (rgb) {
    const chan = (s: string) => Math.min(255, parseInt(s, 10));
    let a = 1;
    if (rgb[4] !== undefined) {
      a = rgb[4].endsWith('%') ? parseFloat(rgb[4]) / 100 : parseFloat(rgb[4]);
      if (!Number.isFinite(a)) return null;
      a = Math.min(1, Math.max(0, a));
    }
    return { r: chan(rgb[1]), g: chan(rgb[2]), b: chan(rgb[3]), a };
  }
  // Token surfaces resolve as oklch() in modern browsers (CSS Color 4
  // computed-value serialization) — reuse the tokens.ts transfer function.
  const okl = oklchToSrgb(v);
  return okl ? { ...okl, a: 1 } : null;
};

/** WCAG sRGB channel linearization. */
const linear = (c: number): number => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const luminance = (c: InkColor): number =>
  0.2126 * linear(c.r) + 0.7152 * linear(c.g) + 0.0722 * linear(c.b);

/** WCAG contrast ratio (1–21); alpha is ignored — composite first via `over`. */
export const contrastRatio = (x: InkColor, y: InkColor): number => {
  const lx = luminance(x);
  const ly = luminance(y);
  const [hi, lo] = lx >= ly ? [lx, ly] : [ly, lx];
  return (hi + 0.05) / (lo + 0.05);
};

/** Source-over alpha compositing. */
const over = (top: InkColor, below: InkColor): InkColor => {
  const a = top.a + below.a * (1 - top.a);
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const chan = (t: number, b: number) => Math.round((t * top.a + b * below.a * (1 - top.a)) / a);
  return { r: chan(top.r, below.r), g: chan(top.g, below.g), b: chan(top.b, below.b), a };
};

/**
 * Effective backdrop behind `el`: climb computed background-colors until an
 * opaque layer, compositing translucent washes; a translucent-only stack (or
 * none) falls back to the root `--background` token. Null = unevaluable
 * (jsdom without inline backgrounds, exotic serializations) — callers skip.
 */
export const resolveSurfaceColor = (el: HTMLElement): InkColor | null => {
  const layers: InkColor[] = [];
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    let bg = '';
    try {
      bg = getComputedStyle(node).backgroundColor;
    } catch {
      return null;
    }
    const parsed = parseInkColor(bg);
    if (!parsed || parsed.a === 0) continue;
    layers.push(parsed);
    if (parsed.a >= 1) break;
  }
  if (layers.length === 0 || layers[layers.length - 1].a < 1) {
    const base = parseInkColor(getToken('background'));
    if (!base) return null;
    layers.push({ ...base, a: 1 });
  }
  return layers.reduceRight((below, top) => over(top, below));
};

/** Nearest AUTHORED (inline) background-color on the element or an ancestor, bounded by `container`. */
const nearestAuthoredBg = (el: HTMLElement, container: HTMLElement): string | null => {
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    const bg = node.style.backgroundColor;
    if (bg) return bg;
    if (node === container) break;
  }
  return null;
};

/**
 * Forward pass. For every element with an inline `color`:
 * - authored background pair present → pair contrast ≥ 3:1 keeps the pair
 *   (imported colour boxes, highlight spans); a poor pair is neutralized;
 * - otherwise ink-vs-surface < 3:1 is neutralized (inline colour cleared →
 *   inherits the token foreground; original stashed in the data attribute).
 * Unparseable inks/backgrounds are left untouched. Runs restore first so a
 * non-light→non-light theme switch re-evaluates against the new surface.
 */
export const neutralizeAuthoredInk = (container: HTMLElement, surface: InkColor | null): void => {
  restoreAuthoredInk(container);
  for (const el of Array.from(container.querySelectorAll<HTMLElement>('[style*="color"]'))) {
    const inline = el.style.color;
    if (!inline) continue; // matched on background-color etc.
    const ink = parseInkColor(inline);
    if (!ink) continue;
    const authoredBg = nearestAuthoredBg(el, container);
    let base: InkColor | null;
    if (authoredBg !== null) {
      const bg = parseInkColor(authoredBg);
      if (!bg) continue; // unevaluable authored pair — leave untouched
      base = bg.a < 1 ? (surface ? over(bg, surface) : null) : bg;
    } else {
      base = surface;
    }
    if (!base) continue; // no evaluable backdrop
    const effectiveInk = ink.a < 1 ? over(ink, base) : ink;
    if (contrastRatio(effectiveInk, base) >= MIN_CONTRAST) continue;
    el.setAttribute(AUTHORED_INK_ATTR, inline);
    el.style.removeProperty('color');
  }
};

/** Reverse pass — reinstate every stashed original (theme back to light). */
export const restoreAuthoredInk = (container: HTMLElement): void => {
  for (const el of Array.from(container.querySelectorAll<HTMLElement>(`[${AUTHORED_INK_ATTR}]`))) {
    const original = el.getAttribute(AUTHORED_INK_ATTR);
    el.removeAttribute(AUTHORED_INK_ATTR);
    if (original) el.style.setProperty('color', original);
  }
};
